// React
import { useCallback, useEffect, useRef, useState } from 'react';

import ReactPlayer from 'react-player';
import { BiError, BiPlay } from 'react-icons/bi';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { Center, Flex, Icon, useMediaQuery, Spinner } from '@chakra-ui/react';

import { Quality } from '../../shared/utils/Types/videoTypes';
import { Controls } from './Controls';
import { Thumbnail } from './Thumbnail';
// Interfaces
import { LeccionInt } from '../../interfaces/CoursesInt';
import { useProgressContex } from '../../shared/context/progress.context';

const PROGRESO_VIDEO = 60 * 5;

let controlsTimeout: any;
const values = { duration: 0, played: 0 };

interface OpenPlayerProps {
  player: React.MutableRefObject<any>;
  src?: string;
  maxSpeed?: number;
  minSpeed?: number;
  leccion?: LeccionInt | null;
  playedSeconds: number;
  enableKeyboard?: boolean;
  onPlay: (e?: any) => void | any;
  onPause: (e?: any) => void | any;
  onStart: (e?: any) => void | any;
  onUpdate: (e?: any) => void | any;
  setPlayedSeconds: (e?: any) => void | any;
  segundosLeccion: number;
}

export const OpenPlayer = ({
  src,
  player,
  leccion,
  onPlay,
  onStart,
  onPause,
  onUpdate,
  maxSpeed = 1,
  minSpeed = 1,
  playedSeconds,
  setPlayedSeconds,
  segundosLeccion,
  enableKeyboard = false,
}: OpenPlayerProps) => {
  const valuesRef = useRef(values);
  const fullscreenHandler = useFullScreenHandle();

  const [error, setError] = useState<boolean>(false);
  const [isMobilePlayer] = useMediaQuery('(max-width: 490px)');

  const [volume, setVolume] = useState<number>(100);
  const [duration, setDuration] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [quality, setQuality] = useState<Quality>('auto');
  const [savedSeconds, setSavedSeconds] = useState<number>();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isFirstPlay, setIsFirstPlay] = useState<boolean>(true);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);

  const [shouldMountPlayer, setShouldMountPlayer] = useState<boolean>(true);
  const [areControlsVisible, setAreControlsVisible] = useState<boolean>(true);
  const [refreshProgressVideo, setRefreshProgressVideo] = useState<boolean>(false);

  const handleKeyboardCallback = useCallback((e: any) => handleKeyboardControls(e), []);

  useEffect(() => {
    setIsReady(false);
    setIsPlaying(false);
    setRefreshProgressVideo(!refreshProgressVideo);
  }, []);

  useEffect(() => {
    setIsFirstPlay(true);

    setError(false);
    setIsPlaying(false);
    setSavedSeconds(segundosLeccion);
    setPlayedSeconds(segundosLeccion);
  }, [leccion?.id, segundosLeccion]);

  useEffect(() => {
    valuesRef.current.duration = duration;
    valuesRef.current.played = playedSeconds;
  }, [duration, playedSeconds]);

  useEffect(() => {
    if (enableKeyboard) window.addEventListener('keydown', handleKeyboardCallback);
    else window.removeEventListener('keydown', handleKeyboardCallback);
  }, [enableKeyboard]);

  useEffect(() => {
    if (shouldMountPlayer && isReady && player?.current) seekTo(playedSeconds);
    if (shouldMountPlayer && player?.current) seekTo(playedSeconds);
  }, [shouldMountPlayer]);

  useEffect(() => {
    const tmout = setTimeout(() => onUpdate(), PROGRESO_VIDEO * 1000);

    return () => clearTimeout(tmout);
  }, [refreshProgressVideo]);

  const showControls = (shouldSetTimeout: boolean) => {
    setAreControlsVisible(true);
    clearTimeout(controlsTimeout);

    if (shouldSetTimeout) controlsTimeout = setTimeout(() => setAreControlsVisible(false), 2000);
  };

  /** Método interno para navegar a los segundos del video marcados. */
  const seekTo = async (seconds: number) => {
    if(seconds)
    try {
      await player?.current?.seekTo(seconds);

      onUpdate();
    } catch (error) {
      console.log('Error al cambiar el tiempo 😅');
    }
  };

  const handleOnReady = async (msg: any) => {
    console.log('⚡ Vídeo preparado');

    /** Verificamos que no vayamos a navegar a unos segundos del video incorrectos. */
    if (savedSeconds !== undefined && savedSeconds >= 0) await seekTo(savedSeconds);

    await setIsPlaying(false);
    setIsReady(true);

    return true;
  };

  function handleOnSeek(seconds: number) {
    setPlayedSeconds(seconds);
  }

  const handleOnPlayClick = () => {
    if (!leccion?.contenido) return;

    setIsPlaying(!isPlaying);

    setIsFirstPlay(false);
    onPlay();
  };

  const handleOnPlay = () => {
    console.log(`📹 Reproduciendo vídeo... La url ${src ? 'SI' : 'NO'} está cargada`);

    setIsPlaying(true);
  };

  function handleOnPause() {
    setIsPlaying(false);
    onUpdate();

    if (onPause) onPause();
  }

  const handleChangeQuality = async (quality: Quality) => {
    await setShouldMountPlayer(false);
    await setQuality(quality);
    await setShouldMountPlayer(true);
  };

  function handleKeyboardControls(event: KeyboardEvent) {
    if (!leccion?.contenido) return;

    switch (event.code) {
      case 'ArrowRight':
        seekTo(Math.min(valuesRef.current.duration, valuesRef.current.played + 60));
        break;
      case 'ArrowLeft':
        seekTo(Math.max(0, valuesRef.current.played - 60));
        break;

      case 'Space':
        setIsPlaying((c) => !c);
        break;
    }
  }

  const handleOnEnterPIP = async () => {
    // Player is set at <ReactPlayer ref={player} ... />
    const video = player.current.getInternalPlayer();

    if (document.pictureInPictureEnabled) {
      try {
        video.requestPictureInPicture();
      } catch (error) {
        console.log('❌ Error', { error });
      }
    } else {
      console.log('Your browser cannot use picture-in-picture right now');
    }
  };

  return error ? (
    <Center color="white" w="100%" bg="#000" style={{ aspectRatio: '16/9' }}>
      <Icon m="20px" fontSize="7xl" as={BiError} />

      <Flex fontWeight="bold" fontSize="3xl">
        Se ha producido un error inesperado
      </Flex>

      <Flex fontSize="xl">Inténtalo de nuevo más tarde</Flex>
    </Center>
  ) : (
    <FullScreen handle={fullscreenHandler} className="fullscreen-container hidden">
      <Flex boxSize="100%" position="relative" align="center">
        {isFirstPlay && <Thumbnail leccion={leccion && leccion} handleOnPlayClick={handleOnPlayClick} />}

        <Flex
          bg="#000"
          align="center"
          boxSize="100%"
          position="relative"
          style={{ aspectRatio: '16/9' }}
          minH={{ base: '248px', sm: '348px' }}
        >
          <ReactPlayer
            ref={player}
            pip={true}
            controls={false}
            url={leccion?.contenido || undefined}
            width="100%"
            muted={isMuted}
            playing={isPlaying}
            stopOnUnmount={false}
            volume={(volume || 0) / 100}
            playbackRate={playbackSpeed || 1}
            height={fullscreenHandler.active ? 'unset' : '100%'}
            onStart={onStart}
            onSeek={handleOnSeek}
            onPlay={handleOnPlay}
            onReady={handleOnReady}
            onPause={handleOnPause}
            onDuration={setDuration}
            onBuffer={() => setIsBuffering(true)}
            onBufferEnd={() => setIsBuffering(false)}
            onProgress={(p) => setPlayedSeconds(p?.playedSeconds)}
            config={{ vimeo: { playerOptions: { quality, responsive: true, pip: true } } }}
            style={{ aspectRatio: '16/9', display: shouldMountPlayer ? 'unset' : 'none' }}
          />

          <Flex
            top="0px"
            left="0px"
            right="0px"
            bottom="54px"
            align="center"
            justify="center"
            cursor="pointer"
            position="absolute"
            onClick={handleOnPlayClick}
            onMouseMove={() => showControls(true)}
            bg={isBuffering || !isPlaying ? 'blackAlpha.400' : 'transparent'}
          >
            {isBuffering && <Spinner size="xl" color="white" />}

            {!isBuffering && !isPlaying && <Icon as={BiPlay} boxSize="90px" color="#FFF" />}
          </Flex>

          <Controls
            volume={volume}
            quality={quality}
            isMuted={isMuted}
            maxSpeed={maxSpeed}
            minSpeed={minSpeed}
            duration={duration}
            isPlaying={isPlaying}
            playedSeconds={playedSeconds}
            playbackSpeed={playbackSpeed}
            isMobilePlayer={isMobilePlayer}
            fullscreenHandler={fullscreenHandler}
            areControlsVisible={areControlsVisible}
            seekTo={seekTo}
            setVolume={setVolume}
            setIsMuted={setIsMuted}
            setIsPlaying={setIsPlaying}
            showControls={showControls}
            setPlaybackSpeed={setPlaybackSpeed}
            handleOnEnterPIP={handleOnEnterPIP}
            handleChangeQuality={handleChangeQuality}
          />
        </Flex>
      </Flex>
    </FullScreen>
  );
};
